import { useRef, useMemo, useState, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Switch } from '@components/Switch';
import { Select } from '@components/Select';
import { Input } from '@components/Input';
import { ExternalLink } from '@components/Link';
import { Typography } from '@components/Typography';
import GoogleMap, { DEFAULT_MAP_CENTER } from '@components/GoogleMap';
import LocationAutocomplete from '@features/LocationAutocomplete';
import { LocatedInRelationType, locatedInRelationTypeLabelKey, googleMapsPlaceUrl } from '@constants/companies';
import { useTranslation, useAddressPoint } from '@hooks';

export const Form = styled('div')({
    maxWidth: 698,
    display: 'grid',
    marginTop: 24,
    columnGap: 24,
    rowGap: 16,
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
        maxWidth: 'none',
    },
});

const FullWidth = styled('div')({
    gridColumn: '1 / -1',
});

const LOCATED_IN_BOUND_DEGREES = 0.009;

const Address = ({ language, countries, countryLockHint = null, pinLocked = false }) => {
    const [selectedCountry, setSelectedCountry] = useState(null);
    const { t } = useTranslation();
    const {
        control,
        setValue,
        watch,
        trigger,
        formState: { dirtyFields, defaultValues },
    } = useFormContext();
    const mapRef = useRef(null);

    const hasBusinessAddress = watch('hasBusinessAddress');
    const latitude = watch('latitude');
    const longitude = watch('longitude');
    const country = watch('country');
    const [addressLine1, addressLine2, city, region, postalCode] = watch([
        'addressLine1',
        'addressLine2',
        'city',
        'region',
        'postalCode',
    ]);
    const locatedInEnabled = watch('locatedInEnabled');
    const locatedIn = watch('locatedIn');

    const pin = useMemo(() => {
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);
        return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    }, [latitude, longitude]);
    const addressEdited = [
        'addressLine1',
        'addressLine2',
        'city',
        'region',
        'postalCode',
        'latitude',
        'longitude',
    ].some(field => dirtyFields[field]);
    const pinFollowsListing = pinLocked && !addressEdited;

    const addressQuery =
        hasBusinessAddress && !pin && addressLine1 && city
            ? [addressLine1, addressLine2, city, region, postalCode, country].filter(Boolean).join(', ')
            : '';

    const {
        point: addressPoint,
        lastPoint: lastAddressPoint,
        isResolving: isResolvingAddress,
    } = useAddressPoint(addressQuery);
    const searchCenter = pin ?? addressPoint;
    const mapCenter = pin ?? lastAddressPoint ?? DEFAULT_MAP_CENTER;

    const locatedInSearchOptions = useMemo(() => {
        if (!searchCenter) return null;
        const { lat, lng } = searchCenter;
        const lngDelta = LOCATED_IN_BOUND_DEGREES / Math.max(Math.cos((lat * Math.PI) / 180), 0.01);
        return {
            types: ['establishment'],
            locationRestriction: {
                north: lat + LOCATED_IN_BOUND_DEGREES,
                south: lat - LOCATED_IN_BOUND_DEGREES,
                east: lng + lngDelta,
                west: lng - lngDelta,
            },
        };
    }, [searchCenter]);

    const handleLocatedInSelect = option => {
        if (!option?.place_id) return;
        const selected = {
            placeId: option.place_id,
            title: option.structured_formatting?.main_text || option.description,
            address: option.structured_formatting?.secondary_text || '',
            mapsUri: googleMapsPlaceUrl(option.place_id),
        };
        if (locatedIn?.placeId === selected.placeId) return;
        setValue('locatedIn', selected, { shouldDirty: true, shouldValidate: true });
    };

    const handleLocatedInClear = () => {
        setValue('locatedIn', null, { shouldDirty: true, shouldValidate: true });
    };

    useEffect(() => {
        if (country) {
            setSelectedCountry(countries.find(item => item.iso2 === country));
        }
    }, [country]);

    const countriesOptions = useMemo(
        () =>
            countries.map(item => ({
                value: item.iso2,
                label: item.translations[language.toLowerCase()] || item.name,
            })),
        [countries],
    );

    const storedRegion = defaultValues?.country === country ? defaultValues?.region : null;
    const regionsOptions = useMemo(() => {
        const options = selectedCountry
            ? selectedCountry.states.map(item => ({ value: item.name, label: item.name }))
            : [];
        return storedRegion && !options.some(option => option.value === storedRegion)
            ? [{ value: storedRegion, label: storedRegion }, ...options]
            : options;
    }, [selectedCountry, storedRegion]);

    return (
        <>
            <Form>
                <Controller
                    control={control}
                    name="country"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            onChange={({ target: { value } }) => {
                                setValue('country', value, { shouldDirty: true });
                                setValue('region', '', { shouldDirty: true });
                            }}
                            options={countriesOptions}
                            label={t('SettingsCompanies.country')}
                            fullWidth
                            disabled={!!countryLockHint}
                            error={!!error}
                            helperText={error ? t(error.message) : countryLockHint ? t(countryLockHint) : null}
                            required
                        />
                    )}
                />
            </Form>
            <Controller
                control={control}
                name="hasBusinessAddress"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={field.value}
                        handleChange={event => {
                            setValue('hasBusinessAddress', event.target.checked, {
                                shouldDirty: true,
                            });
                            trigger(['addressLine1', 'city', 'postalCode', 'locatedIn']);
                        }}
                        label={t('SettingsCompanies.showAddress')}
                    />
                )}
            />
            {hasBusinessAddress && (
                <>
                    <GoogleMap
                        id="address-map"
                        options={{
                            center: mapCenter,
                            zoom: 17,
                        }}
                        onMapLoad={map => {
                            mapRef.current = map;
                        }}
                        style={{ height: 400, maxWidth: 698, marginTop: '8px' }}
                        pinDraggable={!pinFollowsListing}
                        onChange={value => {
                            setValue('latitude', value.latitude, { shouldDirty: true });
                            setValue('longitude', value.longitude, { shouldDirty: true });
                        }}
                    />
                    {pinFollowsListing && (
                        <Typography
                            variant="caption"
                            sx={{ color: 'var(--text-secondary)', display: 'block', marginTop: '4px' }}
                        >
                            {t('SettingsCompanies.pinFollowsGoogle')}
                        </Typography>
                    )}
                    <Form>
                        <Controller
                            control={control}
                            name="addressLine1"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.addressLine1')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="addressLine2"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.addressLine2')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="city"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.city')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="region"
                            render={({ field, ref }) => (
                                <Select
                                    {...field}
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    onChange={({ target: { value } }) => {
                                        setValue('region', value, { shouldDirty: true });
                                    }}
                                    options={regionsOptions}
                                    label={t('SettingsCompanies.state')}
                                    fullWidth
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="postalCode"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.postalCode')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required
                                />
                            )}
                        />
                        <FullWidth>
                            <Controller
                                control={control}
                                name="locatedInEnabled"
                                render={({ field, ref }) => (
                                    <Switch
                                        {...field}
                                        ref={ref}
                                        checked={field.value}
                                        handleChange={event => {
                                            setValue('locatedInEnabled', event.target.checked, { shouldDirty: true });
                                            trigger('locatedIn');
                                        }}
                                        label={t('SettingsCompanies.locatedInToggle')}
                                    />
                                )}
                            />
                        </FullWidth>
                        {locatedInEnabled && (
                            <>
                                <Controller
                                    control={control}
                                    name="locatedIn"
                                    render={({ field: { value, onBlur }, fieldState: { error, isTouched } }) => (
                                        <LocationAutocomplete
                                            label={t('SettingsCompanies.googleAutocomplete')}
                                            required
                                            autoFocus={!value}
                                            disabled={!locatedInSearchOptions}
                                            initialValue={
                                                value ? [value.title, value.address].filter(Boolean).join(', ') : ''
                                            }
                                            autocompleteRequestOptions={locatedInSearchOptions ?? {}}
                                            onSelectOption={handleLocatedInSelect}
                                            onClearPlace={handleLocatedInClear}
                                            onBlur={onBlur}
                                            error={error && isTouched ? t(error.message) : null}
                                            helperText={
                                                !locatedInSearchOptions ? (
                                                    isResolvingAddress ? null : (
                                                        t('SettingsCompanies.locatedInNeedsPin')
                                                    )
                                                ) : value?.placeId ? (
                                                    <ExternalLink variant="caption" href={value.mapsUri}>
                                                        {t('SettingsCompanies.viewOnGoogleMaps')}
                                                    </ExternalLink>
                                                ) : null
                                            }
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="locatedInRelationType"
                                    render={({ field, ref }) => (
                                        <Select
                                            {...field}
                                            ref={ref}
                                            onChange={({ target: { value } }) =>
                                                setValue('locatedInRelationType', value, { shouldDirty: true })
                                            }
                                            label={t('SettingsCompanies.locatedInRelationLabel')}
                                            fullWidth
                                            options={[
                                                LocatedInRelationType.Independent,
                                                LocatedInRelationType.Department,
                                            ].map(value => ({
                                                label: t(locatedInRelationTypeLabelKey(value)),
                                                value,
                                            }))}
                                        />
                                    )}
                                />
                            </>
                        )}
                    </Form>
                </>
            )}
        </>
    );
};

export default Address;
